/**
 * MemberNotificationBell — the member portal's notification tray.
 *
 * Mirrors the staff NotificationBell / NotificationCenter UX (bell + unread
 * badge, a dropdown of recent notifications with per-item mark-read, "mark all
 * read", and a "See all" link to the full page) but is member-authed: it reads
 * the shared user-scoped /api/notifications endpoints through memberApiFetch
 * (member token + X-CBO-ID) — never the staff apiClient.
 *
 * The badge polls GET /api/notifications/summary every ~45s. Clicking a
 * notification marks it read and, if its `link` maps to a member-portal route,
 * navigates there; staff-only links only mark read (never leave the portal).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, BellRing, Check, CheckCheck, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  getMemberNotificationSummary,
  markMemberNotificationRead,
  markAllMemberNotificationsRead,
  mapNotificationLinkToMemberRoute,
  getMemberToken,
  type MemberNotificationSummary,
  type MemberNotification,
} from '@/lib/memberAuth';
import { useAppTranslation } from '@/contexts/TranslationContext';

const POLL_INTERVAL_MS = 45_000;
const DROPDOWN_WIDTH = 340;

interface MemberNotificationBellProps {
  /** When true, render just the icon (sidebar is collapsed). */
  collapsed?: boolean;
}

export function MemberNotificationBell({ collapsed = false }: MemberNotificationBellProps) {
  const navigate = useNavigate();
  const { t } = useAppTranslation();
  const [summary, setSummary] = useState<MemberNotificationSummary | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchSummary = useCallback(async () => {
    if (!getMemberToken()) return;
    try {
      const data = await getMemberNotificationSummary();
      setSummary(data);
    } catch {
      /* transient — keep the last good summary */
    }
  }, []);

  // Poll the summary for the badge.
  useEffect(() => {
    fetchSummary();
    const id = setInterval(fetchSummary, POLL_INTERVAL_MS);
    const onFocus = () => void fetchSummary();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchSummary]);

  const updatePosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const gap = 8;
    // Prefer aligning the dropdown's left edge with the button; clamp to viewport.
    let left = rect.left;
    if (left + DROPDOWN_WIDTH > window.innerWidth - gap) {
      left = window.innerWidth - DROPDOWN_WIDTH - gap;
    }
    if (left < gap) left = gap;
    const top = Math.min(rect.bottom + gap, window.innerHeight - 120);
    setPosition({ top, left });
  }, []);

  // Position + close-on-outside-click while open.
  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    const id = setTimeout(() => document.addEventListener('click', onClick, true), 50);
    return () => {
      clearTimeout(id);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      document.removeEventListener('click', onClick, true);
    };
  }, [isOpen, updatePosition]);

  // Re-fetch when opening so the tray is fresh.
  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen((o) => {
      if (!o) void fetchSummary();
      return !o;
    });
  };

  const handleMarkRead = async (id: number) => {
    try {
      await markMemberNotificationRead(id);
    } catch {
      /* ignore */
    }
    void fetchSummary();
  };

  const handleMarkAllRead = async () => {
    setBusy(true);
    try {
      await markAllMemberNotificationsRead();
    } catch {
      /* ignore */
    } finally {
      setBusy(false);
    }
    void fetchSummary();
  };

  const handleNotificationClick = async (n: MemberNotification) => {
    if (!n.read) {
      try {
        await markMemberNotificationRead(n.id);
      } catch {
        /* ignore */
      }
    }
    const route = mapNotificationLinkToMemberRoute(n.link);
    setIsOpen(false);
    void fetchSummary();
    // Only navigate when the link maps to a member-portal route; staff-only
    // links just get marked read (never leave the portal).
    if (route) navigate(route);
  };

  const unreadCount = summary?.unread_count ?? 0;
  const hasUrgent = (summary?.urgent_unread_count ?? 0) > 0;
  const recent = summary?.recent_unread ?? [];

  const fmtWhen = (iso: string) => {
    try {
      return formatDistanceToNow(new Date(iso), { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        aria-label={t('notifications.title', 'Notifications')}
        title={`${unreadCount} ${t('notifications.unread', 'unread')}`}
        className={`relative flex items-center rounded-[var(--v2-r-sm)] text-ink-700 transition-colors hover:bg-paper-2 hover:text-ink ${
          collapsed
            ? 'h-8 w-8 justify-center'
            : 'w-full gap-2.5 px-[9px] py-[7px] text-[13px] font-medium'
        } ${hasUrgent ? 'text-red-500 hover:text-red-500' : ''}`}
      >
        {hasUrgent ? (
          <BellRing size={14} className="flex-shrink-0" />
        ) : (
          <Bell size={14} className="flex-shrink-0" />
        )}
        {!collapsed && <span className="flex-1 text-left">{t('notifications.title', 'Notifications')}</span>}
        {unreadCount > 0 && (
          <span
            className={`flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white ${
              collapsed ? 'absolute -right-0.5 -top-0.5' : ''
            } ${hasUrgent ? 'bg-red-500' : 'bg-teal-600'}`}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-[10001] flex max-h-[560px] flex-col overflow-hidden rounded-[10px] border border-v2border bg-surface shadow-v2-2"
            style={{ top: position.top, left: position.left, width: DROPDOWN_WIDTH }}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-v2border px-4 py-3">
              <div className="flex items-center gap-2">
                <Bell size={15} className="text-ink-500" />
                <span className="font-display text-sm font-semibold text-ink">
                  {t('notifications.title', 'Notifications')}
                </span>
                {unreadCount > 0 && (
                  <span className="rounded-full bg-teal-100 px-2 py-0.5 text-[11px] font-semibold text-teal-700 dark:bg-teal-900/40 dark:text-teal-300">
                    {unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    disabled={busy}
                    title={t('ui.mark_all_as_read', 'Mark all as read')}
                    className="rounded-[var(--v2-r-sm)] p-1.5 text-ink-500 hover:bg-paper-2 hover:text-ink disabled:opacity-50"
                  >
                    <CheckCheck size={16} />
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  aria-label={t('buttons.close', 'Close')}
                  className="rounded-[var(--v2-r-sm)] p-1.5 text-ink-500 hover:bg-paper-2 hover:text-ink"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* List (recent unread) */}
            <div className="flex-1 overflow-y-auto">
              {recent.length === 0 ? (
                <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
                  <Bell size={28} className="mb-2 text-ink-300" />
                  <p className="text-[13px] font-medium text-ink">
                    {t('notifications.noNewNotifications', 'No new notifications')}
                  </p>
                  <p className="mt-0.5 text-[12px] text-ink-500">
                    {t('notifications.allCaughtUp', "You're all caught up!")}
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-v2border">
                  {recent.map((n) => (
                    <li
                      key={n.id}
                      className={`group px-4 py-3 transition-colors hover:bg-paper-2 ${
                        !n.read ? 'bg-teal-50/40 dark:bg-teal-900/10' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <button
                          onClick={() => handleNotificationClick(n)}
                          className="min-w-0 flex-1 text-left"
                        >
                          <div className="flex items-center gap-1.5">
                            {!n.read && (
                              <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-teal-500" />
                            )}
                            <p className="truncate text-[13px] font-semibold text-ink">{n.title}</p>
                            {n.priority === 'urgent' && (
                              <span className="flex-shrink-0 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300">
                                {t('ui.urgent', 'Urgent')}
                              </span>
                            )}
                          </div>
                          <p className="mt-0.5 line-clamp-2 text-[12px] text-ink-600">{n.message}</p>
                          <p className="mt-1 text-[11px] text-ink-400">{fmtWhen(n.created_at)}</p>
                        </button>
                        {!n.read && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleMarkRead(n.id);
                            }}
                            title={t('notifications.markAsRead', 'Mark as read')}
                            className="mt-0.5 flex-shrink-0 rounded-[var(--v2-r-sm)] p-1 text-ink-400 opacity-0 transition-opacity hover:bg-paper hover:text-teal-600 group-hover:opacity-100"
                          >
                            <Check size={14} />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-v2border p-2 text-center">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/member/notifications');
                }}
                className="w-full rounded-[var(--v2-r-sm)] px-3 py-1.5 text-[13px] font-semibold text-teal-700 hover:bg-paper-2 dark:text-teal-400"
              >
                {t('notifications.viewAll', 'See all notifications')}
              </button>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
